import Button from '@liquidweb-dev/undercurrent/Button';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import { formControlLabelClasses } from '@liquidweb-dev/undercurrent/FormControlLabel';
import RadioTile from '@liquidweb-dev/undercurrent/RadioTile';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import CircleXIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleX';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

import HookedRadioGroup from 'components/Form/HookedRadioGroup';
import HookedTextField from 'components/Form/HookedTextField';
import PaymentMethodBadges from 'components/PaymentMethodBadges';
import { useDialogMakePaymentContext } from '../dialogMakePaymentContext';
import useChosenPaymentMethod from '../useChosenPaymentMethod';
import { type ResolvedPaymentMethodI } from '../usePaymentMethods';
import PaymentMethodDetails from './PaymentMethodDetails';

/** Says the saved methods could not be fetched, and offers another go at it */
const SavedPaymentMethodsFetchError = ({
	onTryAgain,
	sx,
	...rest
}: StackProps & { onTryAgain: () => void }) => (
	<Stack
		sx={combineSx(
			({ borderRadius, vars }) => ({
				flexDirection: { xs: 'column', sm: 'row' },
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 100,
				borderRadius: borderRadius.rounded,
				border: '1px solid',
				borderColor: vars.palette.uc.border.weak,
				padding: 200,
			}),
			sx,
		)}
		{...rest}
	>
		<Stack
			sx={({ vars }) => ({
				flexDirection: 'row',
				alignItems: 'center',
				gap: 100,
				color: vars.palette.uc.danger.text.main,
			})}
		>
			<CircleXIcon
				sx={({ size }) => ({
					flexShrink: 0,
					width: size.sm,
					height: size.sm,
				})}
			/>
			<Typography variant="body-md" sx={{ fontWeight: 500 }}>
				There was a problem fetching your payment method data
			</Typography>
		</Stack>

		<Button size="small" onClick={onTryAgain} sx={{ flexShrink: 0 }}>
			Try again
		</Button>
	</Stack>
);

/** One saved method to pay with, and its CVV when the processor asks for one */
const SavedPaymentMethod = ({
	method,
	checked,
}: {
	method: ResolvedPaymentMethodI;
	checked: boolean;
}) => (
	<Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
		<RadioTile
			value={String(method.instrumentId || '')}
			checked={checked}
			disabled={method.isExpired}
			sx={{
				width: '100%',
				[`& .${formControlLabelClasses.label}`]: { width: '100%' },
			}}
		>
			<Stack
				sx={{
					alignItems: 'center',
					flexDirection: 'row',
					gap: 100,
					justifyContent: 'space-between',
					width: '100%',
				}}
			>
				<PaymentMethodDetails
					paymentMethod={
						method.processor === 'paypal' ? 'paypal' : method.paymentMethod
					}
					issuer={method.issuer}
					label={method.label}
					secondaryLabel={method.secondaryLabel}
				/>
				<PaymentMethodBadges
					isBackup={method.isBackup}
					isDefault={method.isDefault}
					isExpired={method.isExpired}
				/>
			</Stack>
		</RadioTile>

		{/* unmounted once collapsed, so the form only ever holds the one CVV */}
		<Collapse
			in={checked && method.requiresCVV}
			orientation="horizontal"
			timeout={200}
			unmountOnExit
			sx={{ flexShrink: 0 }}
		>
			<HookedTextField
				name="cardCode"
				placeholder="CVV"
				size="large"
				sx={{ width: '72px', marginLeft: 100 }}
			/>
		</Collapse>
	</Stack>
);

/**
 * The account's saved payment methods, whichever processor they belong to - a
 * method already on file stays payable even once its kind can no longer be added.
 */
const SavedPaymentMethods = ({ sx, ...rest }: StackProps) => {
	const { hasPaymentMethodsError, paymentMethods, refetchPaymentMethods } =
		useDialogMakePaymentContext();
	const { chosenInstrumentId } = useChosenPaymentMethod();

	if (hasPaymentMethodsError) {
		return (
			<SavedPaymentMethodsFetchError
				onTryAgain={refetchPaymentMethods}
				sx={sx}
				{...rest}
			/>
		);
	}

	return (
		<HookedRadioGroup
			name="paymentInstrumentId"
			aria-label="Saved payment methods"
			sx={combineSx({ display: 'flex', gap: 100 }, sx)}
			{...rest}
		>
			{paymentMethods.map((method) => (
				<SavedPaymentMethod
					key={method.instrumentId}
					method={method}
					checked={
						(method.instrumentId?.toString() || '') === chosenInstrumentId
					}
				/>
			))}
		</HookedRadioGroup>
	);
};

export default SavedPaymentMethods;
